/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import * as Cesium from "cesium";
import * as React from "react";
import { GeopickingPoint } from "~/api/GeneratedApi";
import {
  CesiumTool,
  setCesiumPopupPosition,
  setSelectedCesiumItem,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";
import { CesiumMapItem, Custom3DTileset } from "../types/CesiumMapItems";
import { CesiumEventType } from "./useCesiumEventHandler";

type UseCesiumGeopickingToolReturn = {
  cesiumGeopickingTool: (args: CesiumGeopickingToolArgs) => void;
};
type CesiumGeopickingToolArgs = {
  viewer: Cesium.Viewer;
  activeTool: CesiumTool | undefined;
  eventType: CesiumEventType;
  position: Cesium.Cartesian2;
};

export function useCesiumGeopickingTool(): UseCesiumGeopickingToolReturn {
  const hoverPointRef = React.useRef<Cesium.Entity>();
  const dispatch = useAppDispatch();

  const cleanup = React.useCallback((viewer: Cesium.Viewer) => {
    if (hoverPointRef.current) viewer.entities.remove(hoverPointRef.current);
    hoverPointRef.current = undefined;
  }, []);

  const cesiumGeopickingTool = React.useCallback(
    ({
      viewer,
      activeTool,
      eventType,
      position,
    }: CesiumGeopickingToolArgs): void => {
      const linepointColor = Cesium.Color.GREEN;
      const pointOutlineColor = Cesium.Color.GREENYELLOW;

      if (activeTool !== "geopicking") {
        cleanup(viewer);
        return;
      }
      // Allow picking only from pointcloud
      const carte3 = viewer.scene.pickPosition(position);
      const drilledFeatures = viewer.scene.drillPick(position, 2, 1, 1);
      const features = drilledFeatures.filter(
        (f) => f.primitive instanceof Cesium.Cesium3DTileset,
      );
      const feature = features[0];
      if (!feature || !(feature.primitive instanceof Cesium.Cesium3DTileset)) {
        cleanup(viewer);
        return;
      }

      const { seaSurveyId } = feature.primitive as Custom3DTileset;
      if (!seaSurveyId) {
        cleanup(viewer);
        return;
      }

      if (eventType === "hover" && carte3) {
        if (!hoverPointRef.current) {
          hoverPointRef.current = viewer.entities.add({
            position: carte3,
            id: "geopicking-new",
            point: {
              color: linepointColor,
              pixelSize: 10,
              outlineWidth: 3,
              outlineColor: pointOutlineColor,
              // heightReference: heightRef,
            },
          });
        } else {
          hoverPointRef.current.position = new Cesium.ConstantPositionProperty(
            carte3,
          );
        }
      }
      if (eventType === "click" && carte3) {
        const item: CesiumMapItem & {
          seaSurvey: { id: number };
          location: GeopickingPoint;
        } = {
          type: "geopicking",
          layer: "geopicking",
          mapId: "geopicking-new",
          location: { x: carte3.x, y: carte3.y, z: carte3.z },
          seaSurvey: { id: seaSurveyId },
        };
        dispatch(setSelectedCesiumItem(item));
        const arrowSize = 10;
        dispatch(
          setCesiumPopupPosition({
            bottom: viewer.canvas.clientHeight - position.y + arrowSize,
            left: position.x - arrowSize * 2,
          }),
        );
      }
    },
    [cleanup, dispatch],
  );

  return { cesiumGeopickingTool };
}
